import styled from "@emotion/styled";
import type { TextFieldProps } from "@mui/material";
import { Popover, TextField } from "@mui/material";
import { extend } from "colord";
import namesPlugin from "colord/plugins/names";
import { debounce } from "es-toolkit";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEventHandler,
  type FocusEventHandler,
  type MouseEvent,
} from "react";
import { useColorPicker } from "react-best-gradient-color-picker";

import { zIndexes } from "../utilities/zIndexes";
import { ColorPicker } from "./ColorPicker";

extend([namesPlugin]);

const GradientPickerContainer = styled.div`
  display: block;
  width: 100%;
`;

const GradientPickerButton = styled.button`
  height: 26px;
  width: 26px;
  padding: 0;
  margin: 0 10px 0 0;
  min-width: 0;
  outline: none;
  border: solid 1px ${({ theme }) => theme.outlineSoft};
  border-radius: 6px;
  cursor: pointer;
  overflow: hidden;

  > span {
    display: block;
    height: 100%;
    width: 100%;
  }
  &:hover {
    border-color: ${({ theme }) => theme.primary};
  }
`;

const GradientLabel = styled.label`
  border-bottom: solid 1px ${({ theme }) => theme.outlineSoft};
  padding: 6px 0 6px;
  width: 100%;
`;

const ColorTextField = styled(TextField)`
  width: 100%;
  height: 100%;
  input {
    text-align: left;
    height: 24px;
  }
`;

const GradientPickerInputContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 30px;

  ${GradientPickerButton} {
    flex: none;
  }
`;

const GradientPickerPopover = styled(Popover)`
  z-index: ${zIndexes.colorPickerPopover};
  .MuiPaper-root {
    overflow: visible;
    border-radius: 8px;
    border: 0;
    background: ${({ theme }) => theme.surface};
  }
`;

const GradientPickerPopoverContent = styled.div`
  padding: 5px;
  position: relative;
  &:after {
    content: "";
    position: absolute;
    top: 50%;
    left: 100%;
    margin-top: -4px;
    border-top: 8px solid transparent;
    border-bottom: 8px solid transparent;
    border-left: 8px solid ${({ theme }) => theme.surface};
  }
`;

export type GradientPickerInputProps = Omit<TextFieldProps, "value" | "onChange"> & {
  onChange: (color: string) => void;
  value: string;
  setError?: (error: string | undefined) => void;
};

export const GradientPickerInput = ({ onChange, value, ...props }: GradientPickerInputProps) => {
  const dataTest = (props as Record<string, string>)["data-test"];

  const [color, setColor] = useState(value);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const { isGradient, setGradient } = useColorPicker(color, setColor);

  const popoverOpen = Boolean(anchorEl && !props.disabled);
  const id = popoverOpen ? "simple-popover" : undefined;

  // Debounce to prevent too many unnecessary config updates.
  const debouncedOnChange = useRef(
    debounce((debouncedColor: string) => {
      if (isGradient && debouncedColor !== value) {
        onChange(debouncedColor);
      }
    }, 300),
  );

  const handlePopoverClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const handleChange: ChangeEventHandler<HTMLInputElement> = useCallback((event) => {
    const newColor = event.target.value;
    setColor(newColor);
  }, []);

  const handleBlur: FocusEventHandler<HTMLInputElement> = () => {
    if (isGradient) {
      onChange(color);
    } else {
      setColor(value);
    }
  };

  const handlePickerChange = useCallback((newColor: string) => setColor(newColor), []);

  const testAttribute = { "data-test": `${dataTest ?? "color"}-input` };

  useEffect(() => {
    debouncedOnChange.current(color);
    return debouncedOnChange.current.cancel;
  }, [debouncedOnChange, color]);

  useEffect(() => {
    if (value !== color) {
      setColor(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    // We kind of want to force gradients here.
    if (!isGradient) setGradient(`linear-gradient(90deg, #fff 0%, #000 100%)`);
  }, [color, isGradient, setGradient]);

  return (
    <GradientPickerContainer>
      <GradientPickerInputContainer>
        {/* ensure the button does not submit (https://github.com/mui/material-ui/issues/27739) */}
        <GradientPickerButton type="button" onClick={handlePopoverClick}>
          <span style={{ background: color }} />
        </GradientPickerButton>
        <GradientLabel>Gradient</GradientLabel>
        {/* NOTE: disabled for now until we can decide if it's better UX to allow users manual gradient input. */}
        {/* <ColorTextField
          {...props}
          value={color}
          onChange={handleChange}
          onBlur={handleBlur}
          spellCheck={false}          
          variant="standard"
          disabled={true} 
          slotProps={{
            htmlInput: testAttribute,
            inputLabel: { shrink: !!color, style: { margin: "2px 0 0 25px" } },
            input: {
              style: { paddingLeft: 0, paddingRight: 0 },
            },
          }}
        /> */}
      </GradientPickerInputContainer>

      <GradientPickerPopover
        id={id}
        open={popoverOpen}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "center",
          horizontal: 270,
        }}
      >
        <GradientPickerPopoverContent data-test="color-picker-container">
          <ColorPicker
            value={color}
            onChange={handlePickerChange}
            hideControls={false}
            hideGradientControls={false}
            hideInputs={false}
            width={230}
            height={230}
          />
        </GradientPickerPopoverContent>
      </GradientPickerPopover>
    </GradientPickerContainer>
  );
};
